import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Switch from "@mui/material/Switch";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import MDAutocomplete from "components/MDAutocomplete";
import api from "services/api";

const emptyForm = {
  kode: "",
  nama: "",
  sektor: "",
  kategori: "PERIZINAN",
  waktu_layanan: "1 Jam",
  output: "Dokumen Izin",
  perlu_rekomendasi_teknis: false,
  perlu_pemeriksaan_lapangan: false,
  status: "AKTIF",
};

const KATEGORI_OPTIONS = ["PERIZINAN", "NONPERIZINAN"];
const STATUS_OPTIONS = ["AKTIF", "NONAKTIF"];

function JenisIzinFormDialog({ open, mode, item, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (mode === "edit" && item) {
        setForm({
          kode: item.kode || "",
          nama: item.nama || "",
          sektor: item.sektor || "",
          kategori: item.kategori || "PERIZINAN",
          waktu_layanan: item.waktu_layanan || "1 Jam",
          output: item.output || "Dokumen Izin",
          perlu_rekomendasi_teknis: !!item.perlu_rekomendasi_teknis,
          perlu_pemeriksaan_lapangan: !!item.perlu_pemeriksaan_lapangan,
          status: item.status || "AKTIF",
        });
      } else {
        setForm(emptyForm);
      }
    }
  }, [open, mode, item]);

  const handleChange = (field) => (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSelectChange = (field) => (value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.kode.trim() || !form.nama.trim() || !form.sektor.trim()) {
      alert("Kode, nama, dan sektor wajib diisi");
      return;
    }

    const payload = {
      ...form,
      perlu_rekomendasi_teknis: form.perlu_rekomendasi_teknis ? 1 : 0,
      perlu_pemeriksaan_lapangan: form.perlu_pemeriksaan_lapangan ? 1 : 0,
    };

    setSaving(true);
    try {
      if (mode === "edit" && item?.id) {
        await api.put(`jenis-izin/${item.id}`, payload);
      } else {
        await api.post("jenis-izin", payload);
      }
      onSaved();
      onClose();
    } catch (err) {
      alert(err.response?.data?.message || "Gagal menyimpan jenis izin");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {mode === "edit" ? "Edit Jenis Izin" : "Tambah Jenis Izin Baru"}
        <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <MDBox component="form" id="jenis-izin-form" onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <MDInput label="Kode *" fullWidth value={form.kode} onChange={handleChange("kode")} />
            </Grid>
            <Grid item xs={12} sm={8}>
              <MDInput label="Nama Layanan *" fullWidth value={form.nama} onChange={handleChange("nama")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput label="Sektor *" fullWidth value={form.sektor} onChange={handleChange("sektor")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDAutocomplete
                label="Kategori"
                fullWidth
                options={KATEGORI_OPTIONS}
                value={form.kategori}
                onChange={handleSelectChange("kategori")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput label="Waktu Layanan" fullWidth value={form.waktu_layanan} onChange={handleChange("waktu_layanan")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDInput label="Output" fullWidth value={form.output} onChange={handleChange("output")} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDAutocomplete
                label="Status"
                fullWidth
                options={STATUS_OPTIONS}
                value={form.status}
                onChange={handleSelectChange("status")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MDBox display="flex" flexDirection="column" gap={1} pt={1}>
                <MDBox display="flex" alignItems="center">
                  <Switch checked={form.perlu_rekomendasi_teknis} onChange={handleChange("perlu_rekomendasi_teknis")} />
                  <MDTypography variant="button">Perlu Rekomendasi Teknis</MDTypography>
                </MDBox>
                <MDBox display="flex" alignItems="center">
                  <Switch checked={form.perlu_pemeriksaan_lapangan} onChange={handleChange("perlu_pemeriksaan_lapangan")} />
                  <MDTypography variant="button">Perlu Pemeriksaan Lapangan</MDTypography>
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton variant="outlined" color="secondary" onClick={onClose}>
          Batal
        </MDButton>
        <MDButton variant="gradient" color="info" type="submit" form="jenis-izin-form" disabled={saving}>
          {saving ? "Menyimpan..." : "Simpan"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

JenisIzinFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.oneOf(["create", "edit"]).isRequired,
  item: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func.isRequired,
};

export default JenisIzinFormDialog;
